import { useEffect, useMemo, useState } from 'react'
import Btn from '../../../../UI/Btn'
import Table from '../../../../UI/Table'
import MiniHeader from '../../../../UI/MiniHeader'

import styles from './style.module.scss'
import Search from '../../../../UI/Search'
import BtnFilter from '../../../../UI/BtnFilter'
import http from '../../../../shared/http'
import Pagination from '../../../../UI/Pagination'
import BtnTooltip from '../../../../UI/BtnTooltip'
import { useTranslation } from 'react-i18next'
import FilterPanel from '../../../../UI/FilterPanel'
import useFilters from '../../../../UI/FilterPanel/useFilters'
import { branchField, companyTypeField, stateField } from '../../../../UI/FilterPanel/fields'
import { TApi, TLanguages, TRoles, TRoutes } from '../../../../shared/const'
import { useIsReadOnly } from '../../../../shared/alumni'
import { toast } from 'react-toastify'
import { useHistory } from 'react-router'
import {
	blobTypes,
	companySupervision,
	customNumberFormat,
	downloadFile,
	personName,
	reportData,
	stateFullLabel,
	stateLabel
} from '../../../../shared/helpers'
import { reportTypeLabel } from '../../../../shared/reportLabel'
import useDebouncedValue from '../../../../shared/useDebouncedValue'
import useLatestOnly from '../../../../shared/useLatestOnly'
import clsx from 'clsx'
import Icon from '../../../../UI/Icon'
import { connect } from 'react-redux'
import { formatVienna } from '../../../../shared/datetime'

function Reports(props) {
	const { t, i18n } = useTranslation()
	const history = useHistory()
	const [search, setSearch] = useState('')
	const [reports, setReports] = useState([])
	const [countAll, setCountAll] = useState(0)
	const [page, setPage] = useState(0)
	const [sort, setSort] = useState({ name: 'companyName', direction: 'asc' })
	const [showFilter, setShowFilter] = useState(false)
	const [loading, setLoading] = useState(false)
	const readOnly = useIsReadOnly()
	const limit = 10

	// Bundesland-Admins sehen ohnehin nur ihr eigenes Bundesland (das setzt das
	// Backend), ein Filter darauf waere eine Auswahl ohne Wirkung.
	const fields = useMemo(
		() => [
			...(props.role === TRoles.ADMIN ? [] : [stateField(t)]),
			companyTypeField(t),
			branchField(t, props.branch, i18n.language)
		],
		[t, i18n.language, props.role, props.branch]
	)

	const filters = useFilters({ storageKey: 'reports', fields })
	const filterQuery = JSON.stringify(filters.query)
	const debouncedSearch = useDebouncedValue(search)
	const latest = useLatestOnly()

	const fetchReports = () => {
		const isCurrent = latest()

		http.get(TApi.ADMIN_GET_REPORTS, {
			params: {
				limit,
				offset: page * limit,
				order: sort.name,
				direction: sort.direction,
				search: debouncedSearch,
				filter: filterQuery
			}
		}).then(({ data }) => {
			if (!isCurrent()) return

			const arrayTable = data.rows.map(v => {
				const formatData = reportData(v.Company.companyType, {
					...v,
					insurance: data.insurance
				})
				return {
					...v,
					...formatData,
					companyName: v.Company.name,
					companyType: v.Company.companyType,
					branch: v.Company.branch,
					state: v.Company.state,
					createdAt: formatVienna(v.createdAt, 'dd.MM.yyyy'),
					school: companySupervision(v.Company)?.Teacher?.School?.name,
					teacher: personName(companySupervision(v.Company)?.Teacher)
				}
			})
			setReports(arrayTable)
			setCountAll(data.count)
		})
	}
	const handleSort = (name, direction) => {
		setSort({ name, direction })
	}
	const handleFilterChange = (key, selected) => {
		setPage(0)
		filters.setField(key, selected)
	}

	const handleFilterReset = () => {
		setPage(0)
		filters.resetAll()
	}

	const handleSearchChange = value => {
		setPage(0)
		setSearch(value)
	}

	// Gefiltert und gesucht wird ohne Knopfdruck; jede Aenderung setzt die Seite
	// zurueck, sonst stuende man auf Seite 4 einer kuerzer gewordenen Liste.
	useEffect(() => {
		fetchReports()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, sort, debouncedSearch, filterQuery])

	const columns = useMemo(
		() => [
			{
				Header: t('Company'),
				accessor: 'companyName',
				Cell: ({ row }) => (
					<div
						style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							width: '100%',
							marginRight: '10px',
							wordBreak: 'break-word'
						}}
					>
						<span style={{ overflow: 'hidden', width: 'auto' }}>{row.values.companyName}</span>
						<BtnTooltip
							items={[
								{
									disabled: !row.original.isSubmitted,
									label: t('View Report'),
									onClick: () => history.push(TRoutes.REPORT_REVIEW(row.original.id))
								},
								{
									// Auch bei bezahlter Rechnung möglich: Zurückweisen
									// löscht sie nicht mehr, sondern storniert sie
									// (Anforderung #91).
									disabled: !row.original.isSubmitted,
									label: t('Reject report'),
									onClick: () => {
										http.put(TApi.ADMIN_UNSUBMIT_REPORTS(row.original.id)).then(({ data }) => {
											// War die Rechnung bezahlt, bleibt das Geld als
											// Guthaben stehen. Ohne diesen Zusatz saehe es
											// aus, als waere es weg (Anforderung #91).
											const guthaben = Math.round((data?.credit || 0) * 100) / 100
											toast.success(
												guthaben > 0
													? t('reportRejectedWithCredit', {
															amount: `€ ${customNumberFormat(guthaben)}`
													  })
													: t('The report was reopened and the invoice cancelled')
											)
											fetchReports()
										})
									}
								},
								{
									disabled: !row.original.isSubmitted,
									label: t('Download report'),
									onClick: () => downloadReport(row.original.id, row.original)
								}
								// Alumni (#213): nur ansehen — kein Zurückweisen, kein Herunterladen.
							].filter((item, index) => !readOnly || index === 0)}
						/>
					</div>
				),
				minWidth: 115
			},
			{
				Header: t('Typ'),
				accessor: 'companyType',
				width: 50
			},
			{
				Header: t('Bericht'),
				accessor: 'type',
				// Umbrechen statt abschneiden: "Halbjahresbericht" endete sonst als
				// "Halbjahre…" (#79).
				Cell: ({ row }) => (
					<span style={{ overflow: 'hidden' }}>
						{reportTypeLabel(row.values.type, row.original.companyType, t)}
					</span>
				),
				width: 145
			},
			{
				Header: t('Branch'),
				accessor: 'branch',
				Cell: ({ row }) => (
					<span style={{ overflow: 'hidden' }}>
						{i18n.language === TLanguages.DE
							? props.branch?.find(v => v.value_EN === row.values.branch)?.value_DE
							: props.branch?.find(v => v.value_EN === row.values.branch)?.value_EN}
					</span>
				),
				width: 100
			},
			{
				Header: t('Fed. State'),
				accessor: 'state',
				// Kurzform (NÖ, OÖ, Stmk …), voller Name beim Darueberfahren.
				cellToolTip: ({ row }) => stateFullLabel(row.original.state),
				Cell: ({ row }) => stateLabel(row.original.state),
				width: 70
			},
			{
				Header: t('Revenues'),
				accessor: 'revenues',
				width: 80,
				Cell: ({ row }) => {
					return <div>{customNumberFormat(row.original.revenues)}</div>
				}
			},
			{
				Header: t('Expanses'),
				accessor: 'expenses',
				width: 80,
				Cell: ({ row }) => {
					return <div>{customNumberFormat(row.original.expenses)}</div>
				}
			},
			{
				Header: t('Result'),
				accessor: 'result',
				width: 80,
				Cell: ({ row }) => {
					return <div>{customNumberFormat(row.original.result)}</div>
				}
			},
			{
				Header: t('Charges'),
				accessor: 'charges',
				width: 80,
				Cell: ({ row }) => {
					return <div>{customNumberFormat(row.original.charges)}</div>
				}
			},
			{
				Header: t('Reported'),
				accessor: 'createdAt',
				width: 85
			},
			{
				Header: t('School'),
				accessor: 'school',
				Cell: ({ row }) => <span style={{ overflow: 'hidden' }}>{row.values.school}</span>,
				minWidth: 140
			},
			{
				Header: t('Teacher'),
				accessor: 'teacher',
				Cell: ({ row }) => <span style={{ overflow: 'hidden' }}>{row.values.teacher}</span>,
				minWidth: 110
			}
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[t, readOnly]
	)

	const downoloadList = fileType => {
		setLoading(true)
		http.get(TApi.ADMIN_DOWNOLOAD_REPORTS_LIST, {
			params: {
				type: fileType,
				leng: localStorage.getItem('i18nextLng')
			},
			responseType: 'arraybuffer'
		})
			.then(res => {
				let blob = new Blob([res.data], {
					type: blobTypes[fileType]
				})
				downloadFile('Reports', blob)
			})
			.finally(() => {
				setLoading(false)
			})
	}

	const downloadReport = (id, data) => {
		const fileName = `Berichte - ${data.companyType} - ${data.companyName} - ${data.type}`
		setLoading(true)
		http.get(TApi.ADMIN_DOWNLOAD_REPORT(id), {
			responseType: 'arraybuffer'
		})
			.then(res => {
				let blob = new Blob([res.data], {
					type: blobTypes.XLS
				})
				downloadFile(fileName, blob)
			})
			.finally(() => {
				setLoading(false)
			})
	}

	return (
		<div className={styles.root}>
			<MiniHeader className={styles.miniHeader} title={t('reports')}>
				<div className={styles.miniHeaderContent}>
					<Search
						value={search}
						onChange={e => handleSearchChange(e.target.value)}
						onSubmit={e => e.preventDefault()}
						placeholder={t('Search reports')}
					/>
					<BtnFilter icon="arrow" onClick={() => setShowFilter(true)} count={filters.count}>
						{t('Filter')} ({filters.count})
					</BtnFilter>
				</div>
			</MiniHeader>
			<div className={styles.subHeader}>
				<Pagination limit={limit} page={page} label={t('reports')} countAll={countAll} onChange={setPage} />
				{!readOnly && (
					<div className={styles.downloadContent}>
						{t('Download list as')}
						<div>
							{!loading && (
								<Btn
									theme="download"
									iconSize={{ w: 24, h: 24 }}
									icon="download"
									onClick={() => {
										downoloadList('XLS')
									}}
								>
									XLS
								</Btn>
							)}
							{!loading && (
								<Btn
									theme="download"
									iconSize={{ w: 24, h: 24 }}
									icon="download"
									onClick={() => {
										downoloadList('PDF')
									}}
								>
									PDF
								</Btn>
							)}
							{loading && (
								<Icon
									className={clsx(styles.icon, styles.rootLoading)}
									size={{ w: 16, h: 16 }}
									name="loading"
									color={'#22404D'}
								/>
							)}
						</div>
					</div>
				)}
			</div>

			<div className="container">
				<Table
					page={page}
					items={reports}
					handleSort={handleSort}
					countAll={countAll}
					onChange={setPage}
					limit={limit}
					columns={columns}
					// Ein Bericht laesst sich erst ansehen, wenn er abgegeben wurde.
					onRowClick={report => history.push(TRoutes.REPORT_REVIEW(report.id))}
					isRowClickable={report => Boolean(report.isSubmitted)}
					rowTitle={t('View Report')}
				/>
			</div>
			<FilterPanel
				open={showFilter}
				onClose={() => setShowFilter(false)}
				fields={fields}
				values={filters.values}
				count={filters.count}
				onChange={handleFilterChange}
				onResetAll={handleFilterReset}
			/>
		</div>
	)
}

const mapStateToProps = state => ({
	branch: state.options.branch,
	role: state.user.role
})

export default connect(mapStateToProps)(Reports)
